import { useMemo } from 'react';
import { useInstallCompatibility } from '../../hooks/useInstallCompatibility';
export function useAppUninstallableReason({
  appContext,
  listingId,
  features: __features,
  published
}) {
  const {
    data,
    loading,
    error
  } = useInstallCompatibility({
    listingId,
    skip: !listingId,
    published,
    appContext
  });
  const uninstallableReason = useMemo(() => {
    var _installCompatibility, _installCompatibility2, _missingUserRoles, _uninstallableReasonD, _uninstallableReasonD2, _productSpecific$miss, _productSpecific$miss2, _ref, _productSpecific$inco;
    const installCompatibility = data === null || data === void 0 ? void 0 : data.productInstallCompatibility;
    if (!installCompatibility) return undefined;
    if (installCompatibility.installed) {
      return {
        label: 'ALREADY_INSTALLED'
      };
    }
    const uninstallableReasonData = (_installCompatibility = installCompatibility.installability) === null || _installCompatibility === void 0 ? void 0 : _installCompatibility.uninstallableReason;
    const portalInstallable = (_installCompatibility2 = installCompatibility.installability) === null || _installCompatibility2 === void 0 ? void 0 : _installCompatibility2.portalInstallable;
    const missingUserRoles = // Field expected at the same level as installability in the return payload
    // Fallback to empty array if not present in the current schema
    (_missingUserRoles = installCompatibility === null || installCompatibility === void 0 ? void 0 : installCompatibility.missingUserRoles) !== null && _missingUserRoles !== void 0 ? _missingUserRoles : [];
    const productSpecific = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.productSpecificUninstallableReason;
    const missingPortalScopes = (_uninstallableReasonD = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.missingPortalScopes) !== null && _uninstallableReasonD !== void 0 ? _uninstallableReasonD : [];
    const missingUserScopes = (_uninstallableReasonD2 = uninstallableReasonData === null || uninstallableReasonData === void 0 ? void 0 : uninstallableReasonData.missingUserScopes) !== null && _uninstallableReasonD2 !== void 0 ? _uninstallableReasonD2 : [];
    const missingPortalScopeGroups = (_productSpecific$miss = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.missingPortalScopeGroups) !== null && _productSpecific$miss !== void 0 ? _productSpecific$miss : [];
    const missingUserScopeGroups = (_productSpecific$miss2 = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.missingUserScopeGroups) !== null && _productSpecific$miss2 !== void 0 ? _productSpecific$miss2 : [];

    // MISSING_HUB_SCOPES if the app is not installable at the portal level based on scopes
    if (missingPortalScopes.length || missingPortalScopeGroups.length) {
      return {
        label: 'MISSING_HUB_SCOPES',
        missingScopes: [...missingPortalScopes, ...missingPortalScopeGroups]
      };
    }

    // MISSING_USER_PERMISSIONS if the app is installable at the portal level but not at the user level based on scopes
    if (portalInstallable && (missingUserScopes.length || missingUserScopeGroups.length)) {
      return {
        label: 'MISSING_USER_PERMISSIONS',
        missingUserRoles
      };
    }

    /**
     * We are leaving the below commented out for now because the BE
     * is planning to map old integrationFeatures onto the UDS V3 features
     * with missing scope group data. In that case, the featureCompatibilities
     * info becomes irrelevant/misleading, so until we've accounted for that
     * more robustly, we're going to skip this NO_COMPATIBLE_FEATURES check
     */

    // NO_COMPATIBLE_FEATURES: if every feature has no overlap between
    // portal tiers and the feature's compatible tiers

    // const portalTiers = installCompatibility.portalTiers || [];
    // const featureCompatibilities: FeatureCompatibility[] =
    //   installCompatibility.featureCompatibilities || [];

    // if (portalTiers.length && featureCompatibilities.length) {
    //   const allFeaturesIncompatible = (
    //     Array.isArray(features) ? features : []
    //   )
    //     .map((currentFeature) => {
    //       const compat = featureCompatibilities.find(
    //         (fc) => fc.featureName === currentFeature.featureName
    //       );
    //       if (!compat) return false; // if unknown, assume compatible
    //       const hasOverlap = compat.compatibleTiers.some((tier: string) =>
    //         (portalTiers as string[]).includes(tier)
    //       );
    //       return !hasOverlap;
    //     })
    //     .every((value) => value === true);

    //   if (
    //     Array.isArray(features) &&
    //     features.length &&
    //     allFeaturesIncompatible
    //   ) {
    //     return { label: 'NO_COMPATIBLE_FEATURES' };
    //   }
    // }

    const incompatibleCombined = (_ref = ((_productSpecific$inco = productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatiblePortalInstalledApps) !== null && _productSpecific$inco !== void 0 ? _productSpecific$inco : []).length ? productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatiblePortalInstalledApps : productSpecific === null || productSpecific === void 0 ? void 0 : productSpecific.incompatibleUserInstalledApps) !== null && _ref !== void 0 ? _ref : [];
    if (incompatibleCombined.length) {
      return {
        label: 'INCOMPATIBLE_INSTALLS',
        incompatibleInstalls: incompatibleCombined
      };
    }
    return undefined;
  }, [data]);
  const missingScopes = useMemo(() => {
    var _installCompatibility3, _installCompatibility4, _installCompatibility5, _installCompatibility6;
    const installCompatibility = data === null || data === void 0 ? void 0 : data.productInstallCompatibility;
    const missingUserScopes = (_installCompatibility3 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility4 = installCompatibility.installability) === null || _installCompatibility4 === void 0 || (_installCompatibility4 = _installCompatibility4.uninstallableReason) === null || _installCompatibility4 === void 0 ? void 0 : _installCompatibility4.missingUserScopes) !== null && _installCompatibility3 !== void 0 ? _installCompatibility3 : [];
    const missingPortalScopes = (_installCompatibility5 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility6 = installCompatibility.installability) === null || _installCompatibility6 === void 0 || (_installCompatibility6 = _installCompatibility6.uninstallableReason) === null || _installCompatibility6 === void 0 ? void 0 : _installCompatibility6.missingPortalScopes) !== null && _installCompatibility5 !== void 0 ? _installCompatibility5 : [];
    const allMissingScopes = [...missingUserScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...missingPortalScopes.map(scope => scope.toLowerCase().replaceAll('_', '-'))];
    return allMissingScopes;
  }, [data]);
  return {
    uninstallableReason,
    missingScopes,
    loading,
    error
  };
}